import React from 'react';
import {
    AlertTriangle, Check, X, Clock, Calendar,
    Shield, Activity, ArrowRight, Zap, Target
} from 'lucide-react';

const ComplianceDigest = ({ digest }) => {
    if (!digest) return null;

    const { applicability, executive_summary, timeline, action_plan } = digest;
    const { is_applicable, reason, confidence } = applicability || {};
    const { urgency_level, risk_level, compliance_deadline, total_effort_estimate } = executive_summary || {};

    const getUrgencyColor = (level) => {
        switch (level?.toLowerCase()) {
            case 'critical': return 'bg-red-600 text-white';
            case 'high': return 'bg-orange-500 text-white';
            case 'medium': return 'bg-amber-500 text-white';
            case 'low': return 'bg-blue-500 text-white';
            default: return 'bg-gray-500 text-white';
        }
    };

    const getRiskColor = (level) => {
        switch (level?.toLowerCase()) {
            case 'high': return 'text-red-600 bg-red-50 border-red-200';
            case 'medium': return 'text-orange-600 bg-orange-50 border-orange-200';
            case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
            default: return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    return (
        <div className="font-sans space-y-8 animate-fadeIn">

            {/* 1. APPLICABILITY & HEADER */}
            <div className={`p-6 rounded-xl border-l-8 shadow-sm ${is_applicable ? 'bg-white border-l-emerald-500' : 'bg-gray-50 border-l-gray-400'}`}>
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h2 className="text-2xl font-bold flex items-center gap-3 text-slate-800">
                            <Shield className={is_applicable ? "text-emerald-600" : "text-gray-400"} size={28} />
                            Rapport de Conformité
                        </h2>
                        <p className="text-slate-500 mt-1">Analyse IA générée pour votre entreprise</p>
                    </div>
                    <span className={`px-4 py-1.5 rounded-full font-bold text-sm uppercase tracking-wide ${is_applicable ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'}`}>
                        {is_applicable ? "Applicable" : "Non Applicable"}
                    </span>
                </div>

                {applicability?.reasoning && (
                    <div className="bg-slate-50 p-4 rounded-lg text-slate-700 text-sm border border-slate-100">
                        <strong>Pourquoi ?</strong> {applicability.reasoning}
                    </div>
                )}
            </div>

            {!is_applicable ? (
                <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <Check size={48} className="mx-auto mb-4 text-gray-300" />
                    <p className="text-lg">Aucune action requise pour cette réglementation.</p>
                </div>
            ) : (
                <>
                    {/* 2. EXECUTIVE SUMMARY GRID */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Urgency */}
                        <div className={`${getUrgencyColor(urgency_level)} p-4 rounded-xl shadow-sm flex flex-col justify-between`}>
                            <div className="flex justify-between items-start opacity-80">
                                <span className="text-xs font-bold uppercase">Urgence</span>
                                <Zap size={18} />
                            </div>
                            <div className="text-2xl font-bold mt-2 capitalize">{urgency_level || 'Normale'}</div>
                        </div>

                        {/* Risk */}
                        <div className={`${getRiskColor(risk_level)} p-4 rounded-xl border shadow-sm flex flex-col justify-between`}>
                            <div className="flex justify-between items-start">
                                <span className="text-xs font-bold uppercase opacity-70">Risque Légal</span>
                                <AlertTriangle size={18} />
                            </div>
                            <div className="text-2xl font-bold mt-2 capitalize">{risk_level || 'Inconnu'}</div>
                        </div>

                        {/* Deadline */}
                        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
                            <div className="flex justify-between items-start text-slate-500">
                                <span className="text-xs font-bold uppercase">Échéance</span>
                                <Calendar size={18} />
                            </div>
                            <div className="text-xl font-bold mt-2 text-slate-800">
                                {compliance_deadline ? new Date(compliance_deadline).toLocaleDateString() : 'Non précisée'}
                            </div>
                        </div>

                        {/* Effort */}
                        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
                            <div className="flex justify-between items-start text-slate-500">
                                <span className="text-xs font-bold uppercase">Charge de Travail</span>
                                <Activity size={18} />
                            </div>
                            <div className="text-xl font-bold mt-2 text-slate-800 capitalize">
                                {total_effort_estimate || 'Variable'}
                            </div>
                        </div>
                    </div>

                    {/* 3. CONTEXT & EXPLANATION */}
                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                        <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-slate-800">
                            <Target className="text-blue-500" size={20} /> Impact Business & Opérationnel
                        </h3>
                        <p className="text-slate-600 leading-relaxed">
                            {executive_summary?.what_it_means || "Analyse indisponible."}
                        </p>
                    </div>

                    {/* 4. TIMELINE */}
                    {timeline?.milestones?.length > 0 && (
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                            <h3 className="font-bold text-lg mb-6 flex items-center gap-2 text-slate-800">
                                <Clock className="text-purple-500" size={20} /> Calendrier de Mise en Conformité
                            </h3>
                            <div className="relative border-l-2 border-slate-200 ml-3 space-y-8">
                                {timeline.milestones.map((milestone, idx) => (
                                    <div key={idx} className="relative pl-8">
                                        <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-purple-500 border-4 border-white shadow-sm"></div>
                                        <div className="text-sm font-bold text-purple-700 mb-1">
                                            {milestone.date ? new Date(milestone.date).toLocaleDateString() : "Date à définir"}
                                        </div>
                                        <h4 className="font-bold text-slate-800">{milestone.title}</h4>
                                        <p className="text-sm text-slate-500 mt-1">{milestone.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 5. ACTION PLAN (Legacy Structure Integration) */}
                    {action_plan && (
                        <div className="space-y-6">
                            <h3 className="font-bold text-xl flex items-center gap-2 text-slate-800 mt-8 border-t pt-8">
                                <Target className="text-emerald-600" /> Plan d'Action Détaillé
                            </h3>

                            {/* MANDATORY */}
                            {action_plan.mandatory_actions?.length > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-xl overflow-hidden">
                                    <div className="bg-red-100/50 p-3 border-b border-red-200 flex items-center gap-2 text-red-800 font-bold uppercase text-xs tracking-wider">
                                        <AlertTriangle size={14} /> Actions Obligatoires
                                    </div>
                                    <div className="p-4 space-y-3">
                                        {action_plan.mandatory_actions.map((action, i) => (
                                            <div key={i} className="bg-white p-4 rounded-lg shadow-sm border border-red-100 flex gap-4">
                                                <div className="mt-1"><ArrowRight className="text-red-500" size={20} /></div>
                                                <div>
                                                    <h4 className="font-bold text-red-900">{action.title}</h4>
                                                    <p className="text-sm text-slate-600 mt-1">{action.description}</p>
                                                    <div className="mt-2 flex gap-4 text-xs text-slate-400">
                                                        {action.responsible && <span>👤Resp: {action.responsible}</span>}
                                                        {action.complexity && <span>⚙️Complexité: {action.complexity}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* RECOMMENDED */}
                            {action_plan.recommended_actions?.length > 0 && (
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl overflow-hidden">
                                    <div className="bg-emerald-100/50 p-3 border-b border-emerald-200 flex items-center gap-2 text-emerald-800 font-bold uppercase text-xs tracking-wider">
                                        <Check size={14} /> Recommandations
                                    </div>
                                    <div className="p-4 space-y-3">
                                        {action_plan.recommended_actions.map((action, i) => (
                                            <div key={i} className="bg-white p-4 rounded-lg shadow-sm border border-emerald-100">
                                                <h4 className="font-bold text-emerald-900">{action.title}</h4>
                                                <p className="text-sm text-slate-600">{action.description}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ComplianceDigest;
